import { ChangeDetectorRef, Component } from "@angular/core";
import { ColDef, ExcelExportParams, ExcelStyle, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { ColParams } from "src/app/shared/grid/ag-grid";
import { ModalService } from "../../shared/modal.service";
import { SharedDataService } from "../../services/sharedDataService";
import { getExportedExcelFileNameSuffix } from "src/app/utility/common-helper";
import { ApprovablePfPlansColumnDefinition } from "./ag-grid-column-definition";
import { PfPlan } from "src/app/generated-models/PfPlan";
import { PfPlanOperationTypeEnum, PfPlanStatusEnum } from "src/app/shared/enums/pf-enums";
import { IPfCapacityPlan } from "src/app/npr-request.model";

@Component({
  standalone: false,
  templateUrl: "./pf-plans-approvable.component.html",
  styleUrls: ["../../styles.scss", "./pf.scss"],
})
export class PfPlansApprovableComponent extends GridViewBaseComponent {
  gridApi: GridApi;
  gridOptions: GridOptions;
  initPageSize: number = 15;
  isApprover: boolean = false;
  showMyServiceTeamPlansOnly: boolean = true;
  showRejected: boolean = false;
  myPfPlanServiceTreeIds: string[] = [];
  keyFilterStats = "filter:approve-pf-plans";
  rowData: IPfCapacityPlan[] = [];
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  selectedRowCount = 0;
  loadCheckBox = false;

  constructor(
    protected apiService: ApiService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    protected sharedDataService: SharedDataService,
    protected modalService: ModalService,
    private cdr: ChangeDetectorRef
  ) {
    super(apiService, sharedDataService);
  }

  async ngOnInit() {
    super.InitAgGrid(ApprovablePfPlansColumnDefinition);

    this.myPfPlanServiceTreeIds = (await this.apiService.getMyPfPlans().toPromise()).map(plan => plan.ServiceTreeId);
    if (this.userProfile?.IsApprover) {
      this.showMyServiceTeamPlansOnly = false;
    }

    ApprovablePfPlansColumnDefinition.forEach( (colDef: ColDef) => {
      if (colDef.headerName === 'Custom Filter') {
        colDef.valueGetter = (params: ColParams<PfPlan, void>) => {
          const row = params.data as PfPlan;
          const isApprovable: boolean = row.Status === PfPlanStatusEnum.Approvable;
          const isRejected: boolean = row.Status === PfPlanStatusEnum.Removed;
          const isInMyServiceTeams: boolean = this.myPfPlanServiceTreeIds.some(service => row.ServiceTreeId === service);
          return (this.showRejected ? isApprovable || isRejected : isApprovable)
            && (this.showMyServiceTeamPlansOnly ? isInMyServiceTeams : true);
        }
      }
    });

    this.gridApi?.setGridOption('columnDefs', ApprovablePfPlansColumnDefinition);
  }

  onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);
    this.gridApi.setFilterModel({
      customFilter: { values: ["true"] },
    });
  }

  onShowRejectedChanged(): void {
    this.gridApi.onFilterChanged();
  }

  refreshData() {
    this.selectedRowCount = 0;
    this.loadingService.setLoading(true);
    this.apiService.getApprovablePfPlans().subscribe(
      (response) => {
        const plans: IPfCapacityPlan[] = [];
        response.forEach((plan) => {
          var row: IPfCapacityPlan = {
            ServiceTeam: plan.ServiceTeam,
            ServiceTreeId: plan.ServiceTreeId,
            Ring: plan.Ring,
            Version: plan.Version,
            Status: plan.Status,
            CreatedBy: "",
            CreatedTime: new Date(0),
            Comment: "",
          };
          var createOperation = plan.Operations.find(p => p.Operation == PfPlanOperationTypeEnum.Create);
          if (createOperation) {
            row.CreatedBy = createOperation.By;
            row.CreatedTime = createOperation.At;
          }
          plans.push(row);
        })
        this.rowData = plans;
        this.loadingService.setLoading(false);
        super.setupFilterByLocalData();
      },
      (e: any) => {
        this.rowData = [];
        this.loadingService.setLoading(false);
        this.notificationService.error(e);
      }
    );
    this.setActionCheckBoxAttributes();
  }

  async batchApprovePfPlan(formValues?: PfPlan[]) {
    const selectedPfPlan: PfPlan[] = formValues ? formValues : this.gridApi.getSelectedRows();
    if (!selectedPfPlan && selectedPfPlan.length === 0) {
      this.message = "Please select PF Blueprint to approve";
      this.notificationService.warning(this.message);
      return;
    }
    const needRefresh: boolean = await this.modalService.pfPlanBatchApprovalModal("Approve PF Blueprints(s)", selectedPfPlan);
    if (needRefresh) {
      this.refreshData();
    }
  }

  onSelectionChanged() {
    this.selectedRowCount = this.gridApi.getSelectedNodes().length;
    this.cdr.detectChanges();
  }

  showPfBatchApproval() {
    if (this.userProfile) {
      return this.userProfile.IsAdmin;
    }
    return false;
  }

  onShowMyServiceTeamPlansOnlyChanged() {
    this.gridApi.onFilterChanged();
  }

  // setActionCheckBoxAttributes call only once by loadCheckBox flag
  setActionCheckBoxAttributes() {
    if (this.showPfBatchApproval() && ApprovablePfPlansColumnDefinition?.length && !this.loadCheckBox) {
      const columnDefs = [];
      this.gridOptions.columnDefs.map((map) => {
        if (map.headerName === "Action") {
          columnDefs.push({
            ...map,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
          });
        } else {
          columnDefs.push(map);
        }
      });

      this.gridApi.setGridOption('columnDefs', columnDefs);
      this.loadCheckBox = true;
    }
  }

  exportExcel() {
    const params = {
      exportMode: "xlsx",
      skipHeader: false,
      columnGroups: false,
      skipGroups: false,
      skipFooters: false,
      skipPinnedTop: false,
      skipPinnedBottom: false,
      allColumns: false,
      onlySelected: false,
      onlySelectedAllPages: false,
      fileName: "ApprovablePfPlans-" + getExportedExcelFileNameSuffix(),
      sheetName: "Approvable PF Execution Plans",
      columnKeys: this.gridApi.getAllGridColumns().filter((c) => c.getColDef().colId !== "action"),
    } as ExcelExportParams;

    this.loadingService.setLoading(true);
    this.gridApi.exportDataAsExcel(params);
    this.loadingService.setLoading(false);
  }
}
